'use client';

/**
 * WebQASourcesCard — Reception citations list.
 *
 * Reads `useDocsStore.response.sources` — the docs the answer cited
 * (title · section · package). Distinct from web_qa_source_doc_canvas,
 * which surfaces the single top source document's raw bytes.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useDocsStore } from '@/lib/stores/docs-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import { QA_SECTION, QA_BOX } from './web-qa-chrome';


export function WebQASourcesCard() {
  const T = useCardTypography();
  const response = useDocsStore((s) => s.response);

  if (!response) return null;

  // Dedupe — the LLM often cites the same doc once per section it drew
  // from, so `sources` arrives with repeats. Key on title (+ section).
  const seen = new Set<string>();
  const sources = (response.sources ?? []).filter((s) => {
    const k = `${s.title ?? ''}|${s.section ?? ''}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  if (sources.length === 0) return null;

  return (
    <section className={QA_SECTION}>
      <div className="w-full">
        <div className={QA_BOX}>
          <div className={cn(T.metadata, 'mb-3 uppercase tracking-wider text-xs')}>
            Sources ({sources.length})
          </div>
          <ul className="flex flex-col gap-1.5">
            {sources.map((src, i) => (
              <li
                key={`${src.semantic_id ?? src.title ?? i}-${i}`}
                className={cn(T.metadata, 'flex items-start gap-2')}
              >
                <span className="text-primary mt-0.5">·</span>
                <span>
                  <span className="font-medium text-foreground">{src.title}</span>
                  {src.section && <span className="text-muted-foreground"> · {src.section}</span>}
                  {src.package && <span className="text-muted-foreground/70"> · {src.package}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
